import axios from 'axios'
import { BASE_URL } from '../../utils/constants/url'
import generalHeader from '../../utils/constants/generalHeader'
import localStorageIds, {
  ORG_SCOPED_LOCAL_STORAGE_KEYS,
} from '../../utils/constants/localStorageIds'
import localStorageHelper from '../../utils/helpers/localStorageHelper'
import {
  removeLocalStorageValue,
  setLocalStorageValue,
} from '../../utils/helpers/localStorageHelperMethods'
import { sharedRefreshToken } from '../../Apis/axiosInterceptor/axiosInstance'
import queryClient from '../../queryClient'
import startSession from '../../utils/helpers/startSession'

const authProvider = {
  // called when the user attempts to log in
  login: async (values) => {
    const url = `${BASE_URL}/auth/login`

    return axios
      .post(url, values, {
        headers: generalHeader(),
        withCredentials: true,
      })
      .then(({ data }) => startSession(data))
      .catch((error) => {
        const { data, status } = error?.response || {}

        if (status === 401 && data === 'Unconfirmed email') {
          setLocalStorageValue([
            { id: localStorageIds.USER_INITIAL_UNCONFIRMED_EMAIL, value: values.username },
          ])
          return Promise.resolve()
        }

        return Promise.reject(data)
      })
  },

  // Method to refresh the access token
  refreshAccessToken: async () => {
    try {
      return await sharedRefreshToken()
    } catch (error) {
      removeLocalStorageValue([localStorageIds.TOKEN, localStorageIds.ADDONS])
      return Promise.reject(error)
    }
  },

  // called when the user clicks on the logout button
  logout: () => {
    removeLocalStorageValue([
      localStorageIds.TOKEN,
      localStorageIds.IMPERSONATION,
      localStorageIds.ADDONS,
      localStorageIds.ORG_PERMISSIONS,
      localStorageIds.COMPETITION_PERMISSIONS,
      'permissions',
      // Drop org-scoped selections so the next user can't inherit this user's
      // federation via the Org-Id header. startSession also clears these on the
      // next login; clearing here too removes them the moment we log out.
      ...ORG_SCOPED_LOCAL_STORAGE_KEYS,
    ])

    // The QueryClient outlives the session on SPA logout — drop the cache so
    // the next user can't be served this user's data. Cancel now but defer
    // clear() past the redirect to /login, so still-mounted observers don't
    // refetch a rebuilt-empty cache with no token.
    queryClient.cancelQueries()
    setTimeout(() => queryClient.clear(), 0)

    return Promise.resolve()
  },

  // called when the API returns an error
  checkError: async ({ status }) => {
    if (status === 401) {
      try {
        await authProvider.refreshAccessToken()
        return Promise.resolve()
      } catch {
        localStorage.removeItem(localStorageIds.TOKEN)
        return Promise.reject() // Token refresh failed, reject promise
      }
    }
    return Promise.resolve()
  },
  // called when the user navigates to a new location, to check for authentication
  checkAuth: () => {
    const { localStorageValue: userInitialUnconfirmedEmail } = localStorageHelper(
      localStorageIds.USER_INITIAL_UNCONFIRMED_EMAIL,
    )

    if (userInitialUnconfirmedEmail) {
      return Promise.reject({
        redirectTo: `send-confirmation-email?initialEmail=${userInitialUnconfirmedEmail}`,
        // message: false suppresses React Admin's ra.auth.auth_check_error
        // ("Authorization Error") notification — this is an intentional redirect.
        message: false,
      })
    }

    // No token means the user is simply logged out. Reject with message: false
    // so React Admin redirects to /login without flashing "Authorization Error"
    // (useCheckAuth only skips the notification when error.message === false).
    return localStorage.getItem(localStorageIds.TOKEN)
      ? Promise.resolve()
      : Promise.reject({ message: false })
  },
  // called when the user navigates to a new location, to check for permissions / addons
  getPermissions: () => {
    const { localStorageValue: addons } = localStorageHelper(localStorageIds.ADDONS)

    const userAddons = localStorage.getItem(localStorageIds.ADDONS) !== 'undefined' ? addons : null
    return userAddons?.length ? Promise.resolve(userAddons) : Promise.reject()
  },
}

export default authProvider
